import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Image from "next/legacy/image";
import Script from "next/script";

export default function Article({}) {
  const content = () => {
    return (
      <>
        <div className={styles["Article__image"]}>
          <Image
            src={"/static/images/articles/urban-development-homelessness.jpeg"}
            alt={"Homeless person on sidewalk"}
            width={900}
            height={565}
          />
        </div>
        <h1>Urban Planning Optimization: Homelessness</h1>
        <ArticleAuthor />
        <p>
          Homelessness impacts more than half a million Americans. Some are
          temporarily homeless, while others are chronically homeless. That
          means they have been without a home for a year or more. People of all
          ages are homeless, even though many work full-time jobs. One major
          cause of homelessness is the lack of affordable housing. Good urban
          planning can address this issue and help reduce the number of citizens
          without a safe place to sleep.
        </p>
        <h2>What is Urban Planning?</h2>
        <p>
          Urban planning is sometimes called regional, town, rural, or city
          planning. It involves political and technical aspects because it
          decides the best and most appropriate use of resources such as land
          and air. Infrastructure like sewage systems, roads, and public
          transportation are all elements of urban planning. Today, urban
          planners focus on sustainable development that considers ecological
          and environmental concerns.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.archdaily.com/984049/what-is-urban-planning">
                What Is Urban Planning?
              </a>
              : Urban planning focuses on the structuring and use of urban land.
            </p>
          </li>
          <li>
            <p>
              <a href="https://education.nationalgeographic.org/resource/urban-planning">
                Urban Planning
              </a>
              : Repurposing space is a large component of good urban planning.
            </p>
          </li>
          <li>
            <p>
              <a href="https://earth.org/urban-planning-and-smart-cities/">
                Urban Planning and Smart Cities
              </a>
              : More than half of all people on earth live in an urban area.
            </p>
          </li>
          <li>
            <p>
              <a href="https://sopa.tulane.edu/blog/role-urban-planners">
                The Role of Urban Planners
              </a>
              : Urban planners must work with various stakeholders to create
              workable plans for the areas.&nbsp;
            </p>
          </li>
          <li>
            <p>
              <a href="https://sppa.uiowa.edu/about-us/what-urban-planning">
                What is Urban and Regional Planning?
              </a>
              &nbsp;Urban planning is an outgrowth of public health and came
              about as a way to stop pandemics caused by poor sanitation and
              other issues.
            </p>
          </li>
        </ul>
        <h2>Factors Preventing Affordable Housing</h2>
        <p>
          Zoning laws and a focus on buildings that will bring in more tax
          revenues are some of the reasons more affordable housing isn't
          available. The U.S. Department of Housing and Urban Development
          suggests expanding the Low-Income Housing Tax Credit to encourage
          builders to construct more housing aimed at people who make about 60%
          of a given area's median income. Another huge issue is the loss of
          affordable housing to gentrification. Housing units are either
          renovated to appeal to higher-income residents, torn down and replaced
          with more expensive housing, or repurposed.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.usich.gov/resources/uploads/asset_library/Housing-Affordability-and-Stablility-Brief.pdf">
                The Importance of Housing Affordability and Stability for
                Preventing and Ending Homelessness
              </a>
              &nbsp;(PDF): Eight million low-income households were paying more
              than 50% of their income for housing in 2017, a situation that
              left them vulnerable to homelessness.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.strongtowns.org/journal/2018/4/10/4-factors-that-can-make-affordable-housing-not-so-affordable">
                4 Factors That Can Make Affordable Housing not so Affordable
              </a>
              : Affordable housing often ends up in areas without access to
              public transportation, jobs, or childcare.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.cbpp.org/research/housing/addressing-the-affordable-housing-crisis-requires-expanding-rental-assistance-and">
                Addressing the Affordable Housing Crisis Requires Expanding
                Rental Assistance and Adding Housing Units
              </a>
              : From 2017-2022 rental prices rose almost 33%, making the
              affordable housing crisis worsen.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.americanprogress.org/article/the-rental-housing-crisis-is-a-supply-problem-that-needs-supply-solutions/">
                The Rental Housing Crisis Is a Supply Problem That Needs Supply
                Solutions
              </a>
              : One issue with affordable housing is that there's not enough of
              it.
            </p>
          </li>
        </ul>
        <div className={styles["Article__image"]}>
          <Image
            src={"/static/images/articles/town-planning.jpeg"}
            alt={"Town planning model"}
            width={600}
            height={365}
          />
        </div>
        <h2>How Zoning Impacts Homelessness</h2>
        <p>
          Zoning regulations tend to work against building affordable housing
          units. Many areas don't want to build multi-family housing because
          they fear it will bring down the property values of their
          single-family homes. Zoning also tends to separate residential and
          commercial spaces, which separates low-income people from job
          opportunities. Some communities use zoning to discourage public
          transportation which causes issues for lower-income residents.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://invisiblepeople.tv/how-zoning-perpetuates-homelessness/#:~:text=By%20doing%20so%2C%20exclusionary%20zoning,extremely-low-income%20earners.">
                How Zoning Perpetuates Homelessness
              </a>
              : One-way zoning causes homelessness is when codes care more about
              what a building looks like than its overall economic impact on the
              city and its housing stock.
            </p>
          </li>
          <li>
            <p>
              <a href="https://northwestcompass.org/zoning-homeless-people/">
                Zoning Out the Homeless People
              </a>
              : Zoning practices often lead to less availability of affordable
              housing for a city's population.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.brookings.edu/blog/social-mobility-memos/2016/08/16/zoning-as-opportunity-hoarding/">
                How Land Use Regulations Are Zoning Out Low-Income Families
              </a>
              : Zoning laws that limit density often indirectly lead to
              increased homeless families.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.theatlantic.com/ideas/archive/2022/06/zoning-housing-affordability-nimby-parking-houston/661289/">
                Cancel Zoning
              </a>
              :&nbsp;<em>The Atlantic&nbsp;</em>claims that zoning is
              responsible for issues like segregation and lack of affordable
              housing.
            </p>
          </li>
          <li>
            <p>
              <a href="https://housingmatters.urban.org/articles/how-zoning-shapes-our-lives">
                How Zoning Shapes our Lives
              </a>
              : Zoning shapes what buildings are allowed in a city and where
              they are allowed to be, often invisibility impacting the lives of
              the people in the area.
            </p>
          </li>
        </ul>
        <h2>Homelessness Statistics</h2>
        <p>
          One troubling statistic is that about half of all homeless people
          work. Many have full-time jobs. They're homeless because working isn't
          enough for them to afford stable housing. Since 2020, housing prices
          have risen dramatically, worsening the crisis of the working homeless.
          There is currently nowhere in the country where a minimum wage job is
          enough to rent a one-bedroom apartment.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://endhomelessness.org/homelessness-in-america/homelessness-statistics/state-of-homelessness/">
                State of Homelessness: 2022 Edition
              </a>
              : More than half a million people were homeless at the beginning
              of 2020.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.dca.ga.gov/safe-affordable-housing/homeless-special-needs-housing/point-time-homeless-count">
                Point in Time Homeless Count
              </a>
              : Local governments record the number of homeless people in their
              area.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.npr.org/2018/09/30/652572292/working-while-homeless-a-tough-job-for-thousands-of-californians">
                Working While Homeless: A Tough Job For Thousands Of
                Californians
              </a>
              : Many working people in cities like{" "}
              <a href="https://www.realestateagents.com/real-estate-agents/los-angeles-ca">
                Los Angeles
              </a>{" "}
              and{" "}
              <a href="https://www.realestateagents.com/real-estate-agents/sacramento-ca">
                Sacramento
              </a>{" "}
              can't afford stable housing.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.thebautistaprojectinc.org/post/homeless-and-employed">
                Being Homeless and Employed: Understanding the Struggles of the
                Working Poor
              </a>
              : 50% of all homeless adults have jobs.
            </p>
          </li>
        </ul>
        <h2>Impact of Homelessness</h2>
        <p>
          In addition to the homeless people themselves, homelessness also
          impacts the life of a city or region. Governments spend a lot of money
          on homeless people, although very little of it goes to helping them
          secure housing. Areas where the homeless gather can frighten away
          other people. People living in areas without sanitation can lead to
          public health crises.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://depts.washington.edu/triolive/quest/2007/TTQ07033/effects.html">
                Effects of Homelessness
              </a>
              : Homelessness impacts every aspect of a person's life.
            </p>
          </li>
          <li>
            <p>
              <a href="https://ofhsoupkitchen.org/how-homelessness-affects-society">
                How Homelessness Affects Society
              </a>
              : Homelessness costs governments a lot of money and can present
              public safety risks.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.bu.edu/sph/news/articles/2016/homelessness-its-consequences-and-its-causes/">
                Homelessness, Its Consequences, and Its Causes
              </a>
              : Almost a quarter of all homeless people are minors under the age
              of eighteen.
            </p>
          </li>
          <li>
            <p>
              <a href="https://my.neighbor.org/what-is-the-cost-of-homelessness/">
                What is the Cost of Homelessness?
              </a>
              : According to one study done in 2016, each homeless person costs
              the taxpayer about $35,000 a year.
            </p>
          </li>
        </ul>
        <h2>The Role of Urban Design in Reducing Homelessness</h2>
        <p>
          Urban design can work to lessen the number of homeless people. One way
          to do that is to prioritize keeping the existing stock of affordable
          housing units and finding ways to build more. Designing cities that
          rely less on cars also helps people reduce their transportation costs,
          which can open up their budgets and help them secure good, permanent
          housing.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://www.atlcitydesign.com/blog/2021/11/2/an-interconnected-community-art-city-building-and-impactful-approaches-to-homelessness">
                An Interconnected Community: Art, City Building and Impactful
                Approaches to Homelessness
              </a>
              : Inclusive city building considers the needs of all city
              dwellers.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.feantsaresearch.org/download/feantsa-ejh2009-article-64990301545660724506.pdf">
                How Urban Planning Instruments Can Contribute in the Fight
                Against Homelessness. An International Overview of Inclusionary
                Housing
              </a>
              &nbsp;(PDF): Good urban planning can reduce the number of people
              without permanent homes.
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.planning.org/policy/guides/adopted/homelessness.htm">
                APA Policy Guide on Homelessness
              </a>
              : The American Planning Association believes that urban planners
              have a responsibility to help end homelessness.
            </p>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "Urban Planning Optimization: Homelessness | RealEstateAgents.com"
        }
        description={
          "There are many factors to consider in supporting the unhoused community and others experiencing homelessness. Find out what variables contribute and more here."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/urban-planning-optimization-homelessness",
          },
          headline: "Urban Planning Optimization: Homelessness",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
